// @school-apps/core/images/limits — the card-image limits, in ONE place, CLIENT-SAFE.
//
// Split out at IMG·4 because two very different consumers need the same numbers: the server route
// that REFUSES an oversize attach (core/images/routes.js, Node) and Dylan's AnkiConnect adapter
// that refuses to push oversize media (apps/dylan/src/anki.js, a browser bundle). This file imports
// nothing at all, so it rides a client bundle without dragging `node:http` behind it — the XR·1d
// client-safe rule, enforced by core/lib/client-safe.test.js.
//
// One owner, not two copies: a cap that disagrees with itself between the server and the exporter
// is a bug that only shows up in the field, on a real card, in front of a kid.

/**
 * The hard ceiling on a stored card image, in DECODED bytes. The client aims for ~200KB; 300KB is
 * headroom that keeps an honest downscale from being refused over a rounding difference while still
 * bounding what rides sync. A card image replicates to every device and lives in the change-log
 * forever — this number is what keeps that sustainable.
 */
export const CARD_IMAGE_MAX_BYTES = 300 * 1024;

/** What a card image may be. Matches the capture path's image types (C·2) — no SVG, ever. */
export const CARD_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * IMG·Q·3 — the near-black threshold: mean luminance (0..1) below which a suggested thumbnail is
 * dropped from the grid. THE FIELD INCIDENT: Dylan reported the image finder returning "mostly-black
 * images" — 19th-century plate scans whose page is almost entirely dark. They are not pictures of
 * anything he can study, and they waste a grid slot each.
 *
 * DELIBERATELY CONSERVATIVE, because the false positive is expensive: an MRI, a CT slice, a
 * dark-field micrograph and a fluorescence image are all mostly black WITH a bright structure — and
 * they are exactly the pictures a dental student wants. At 0.04 a scan of a dark plate goes and
 * those survive. Lives here, in the client-safe module, because all three kid bundles need the SAME
 * number and a threshold that disagrees with itself between apps is a field-only bug.
 */
export const NEAR_BLACK_LUMINANCE = 0.04;

/** The square the luminance sample is drawn into. 16x16 is plenty to judge "is this black". */
export const LUMINANCE_SAMPLE_PX = 16;

/** File extension for a stored image's media filename (Anki media, IMG·4). */
export const IMAGE_EXTENSIONS = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

/**
 * IMG·S·4 — the image host allowlist, moved HERE (client-safe, imports nothing) because the SAME
 * three names now gate two runtimes: the server's provider fetches (core/images/providers.js
 * re-exports this as ALLOWED_HOSTS) and the browser's post-fetch redirect check in
 * fetchAndDownscale. Two copies of an allowlist drift; this is the one owner.
 */
export const CARD_IMAGE_HOSTS = Object.freeze([
  "commons.wikimedia.org", // the Commons MediaWiki API
  "upload.wikimedia.org",  // Commons image bytes + thumbnails
  "api.openverse.org",     // the Openverse API and its thumbnail proxy
]);

/**
 * IMG·S·4 — is this URL one the image system may fetch? https, DEFAULT port only (an explicit
 * non-443 port is a smuggle onto a different service on an allowed host), and an allowlisted
 * hostname. The browser calls this on the RESPONSE url after a fetch, so a redirect the platform
 * followed anyway cannot land bytes from an off-allowlist origin.
 * @param {unknown} url
 * @returns {boolean}
 */
export function isAllowedImageUrl(url) {
  let u;
  try { u = new URL(String(url)); } catch { return false; }
  return u.protocol === "https:" && u.port === "" && CARD_IMAGE_HOSTS.includes(u.hostname);
}
